import React, { useState } from 'react';
import styled from 'styled-components';
import Toaster from 'components/Toaster';
import { generateSekretKey } from 'shared/generateSecretKey';
import storage from 'shared/storage';
import AuthPhoneInput from '../components/AuthPhoneInput';
import RequestCode from '../gql/request-code';
import HelpLink from './components/HavingTrouble';
import SplitScreenLayout from './components/SplitScreenLayout';

const extractRequestErrorMessage = (error, fallbackMessage) => {
  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;

  const graphQLErrorMessage = error?.graphQLErrors?.[0]?.message;
  if (graphQLErrorMessage) return graphQLErrorMessage;

  const networkErrorMessage = error?.networkError?.result?.errors?.[0]?.message;
  if (networkErrorMessage) return networkErrorMessage;

  if (typeof error?.message === 'string' && error.message.length) {
    return error.message;
  }

  return fallbackMessage;
};

const ContinueButton = styled.button`
  display: block;
  width: 100%;
  padding: 16px;
  margin-top: 20px;
  background: #fa4616;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  font-family: ${(props) =>
    props.theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: background 0.2s;

  &:hover {
    background: #e03e12;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 900px) {
    font-size: 17px;
  }
`;

const StepOne = ({
  requestCodeV2,
  setPhoneNumber,
  phoneNumber,
  setStep,
  countryCode,
  setCountryCode,
}) => {
  const [toaster, setToaster] = useState({
    isOpen: false,
    type: 'error',
    title: '',
    message: '',
  });

  const showErrorToaster = (error, fallbackMessage) => {
    setToaster({
      isOpen: true,
      type: 'error',
      title: 'Unable to send code',
      message: extractRequestErrorMessage(error, fallbackMessage),
    });
  };

  return (
    <>
      <SplitScreenLayout
        title="Enter Your Mobile Number"
        subtitle="You will receive a one time password via text message"
      >
        <AuthPhoneInput
          variant="split"
          phoneNumber={phoneNumber}
          countryCode={countryCode}
          onPhoneNumberChange={setPhoneNumber}
          onCountryCodeChange={setCountryCode}
        />

        <ContinueButton
          disabled={!phoneNumber}
          onClick={async () => {
            try {
              const secretKey = await generateSekretKey();

              const result = await requestCodeV2(
                {
                  phone: phoneNumber,
                  calling_code: countryCode || '1',
                },
                secretKey
              );

              if (result?.data?.requestCodeV2) {
                storage.set('promoter-user-phone', {
                  phone: phoneNumber,
                  calling_code: countryCode || '1',
                });
                setStep('StepTwo');
                return;
              }

              showErrorToaster(
                null,
                'Please contact info@pitpay.com to request access to the platform.'
              );
            } catch (error) {
              showErrorToaster(
                error,
                'Please contact info@pitpay.com to request access to the platform.'
              );
            }
          }}
        >
          Continue &rarr;
        </ContinueButton>

        <HelpLink />
      </SplitScreenLayout>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ ...toaster, isOpen: false })}
        title={toaster.title}
        message={toaster.message}
        type={toaster.type}
        duration={5000}
      />
    </>
  );
};

export default RequestCode(StepOne);
